import type { StylingReviewListItem, StylingReviewListQuery } from '@cortex/domain-ac-mens-hub-api';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStylingReviewList } from '../hooks/useStylingReview.js';

/**
 * 選定レビューの一覧。
 *
 * 毎日の全選定を上から順に潰すための画面なので、既定は「未確認」に絞り、
 * 未確認件数を最上部に大きく出す。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 毎日の選定を漏れなく確認するための入口。未確認を上から順に開いていける形にする。
 * @graph-connects useStylingReviewList [calls] 選定一覧の取得
 */

/**
 * 一覧の絞り込みタブ。翻訳キーは common namespace。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 一覧の絞り込みタブの定義。未確認から始めて、答え合わせや初回に切り替えられるようにする。
 * @graph-connects none
 */
export const STYLING_REVIEW_FILTERS: readonly {
  value: StylingReviewListQuery['filter'];
  labelKey: string;
}[] = [
  { value: 'unread', labelKey: 'stylingReview.filter.unread' },
  { value: 'answered', labelKey: 'stylingReview.filter.answered' },
  { value: 'mismatched', labelKey: 'stylingReview.filter.mismatched' },
  { value: 'first', labelKey: 'stylingReview.filter.first' },
  { value: 'all', labelKey: 'stylingReview.filter.all' },
];

/**
 * 見立てと実績が食い違っているか。両方が揃っている行だけを対象にする。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 見立てと実績が食い違った選定を見分ける。片方しか無い行は判定の対象にしない。
 * @graph-connects none
 */
export function isMismatched(item: StylingReviewListItem): boolean {
  return (
    item.myVerdict !== null &&
    item.customerVerdict !== null &&
    item.myVerdict !== item.customerVerdict
  );
}

/**
 * お客様の体格を1行にまとめる。カルテ未登録なら null。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business お客様の体格を一覧の1行に収まる短さでまとめる。未登録は表示を分けるため null にする。
 * @graph-connects none
 */
export function formatBody(height: number | null, weight: number | null): string | null {
  if (height === null && weight === null) return null;
  const parts: string[] = [];
  if (height !== null) parts.push(`${String(height)}cm`);
  if (weight !== null) parts.push(`${String(weight)}kg`);
  return parts.join(' ');
}

/**
 * 見立て / 実績のバッジ。
 * @graph-connects none
 */
function VerdictBadge({ verdict, label }: { verdict: 'good' | 'bad'; label: string }) {
  const tone =
    verdict === 'good'
      ? 'bg-[var(--color-success-solid)] text-[var(--color-success)] border-[var(--color-success)]'
      : 'bg-[var(--color-error-solid)] text-[var(--color-error)] border-[var(--color-error)]';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-[13px] font-medium ${tone}`}
    >
      {label}
    </span>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定の一覧を新しい順に並べ、未確認の残り件数を示して1件ずつ開かせる。
 * @graph-connects useStylingReviewList [calls] 選定一覧の取得
 */
export function StylingReviewListPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<StylingReviewListQuery['filter']>('unread');
  const { data, isLoading, error } = useStylingReviewList(filter);

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-text-muted)]">
        {t('app.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-error)]">
        {t('common.fetchError')}
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-primary)]">
            {t('stylingReview.title')}
          </h1>
          <p className="mt-1 text-[14px] text-[var(--color-text-muted)]">
            {t('stylingReview.description')}
          </p>
        </div>
        <div className="flex items-baseline gap-3 rounded-lg border border-[var(--color-accent-primary)] bg-[var(--color-accent-muted)] px-5 py-3">
          <span className="text-[28px] font-semibold tabular-nums text-[var(--color-accent-primary)]">
            {data?.unreadCount ?? 0}
          </span>
          <span className="text-[14px] text-[var(--color-text-secondary)]">
            {t('stylingReview.unread')}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {STYLING_REVIEW_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            aria-pressed={filter === f.value}
            onClick={() => {
              setFilter(f.value);
            }}
            className={`rounded-full border px-4 py-2 text-[14px] font-medium transition-colors ${
              filter === f.value
                ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)] text-white'
                : 'border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-bg-surface)] p-8 text-center text-[14px] text-[var(--color-text-muted)]">
          {t('stylingReview.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-[var(--color-border)]">
          {items.map((item) => {
            const body = formatBody(item.height, item.weight);
            return (
              <Link
                key={item.phoenixOrderId}
                to="/styling-review/$phoenixOrderId"
                params={{ phoenixOrderId: String(item.phoenixOrderId) }}
                className={`flex flex-wrap items-center gap-4 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] px-5 py-4 last:border-b-0 hover:bg-[var(--color-bg-hover)] ${
                  item.isRead ? '' : 'shadow-[inset_4px_0_0_var(--color-accent-primary)]'
                }`}
              >
                <span className="w-[150px] shrink-0 text-[14px] tabular-nums text-[var(--color-text-secondary)]">
                  {item.completedAt.slice(0, 16).replace('T', ' ')}
                </span>
                <span className="min-w-[190px] flex-1">
                  <span className="text-[16px] font-medium text-[var(--color-text-primary)]">
                    {item.customerName ?? String(item.arthurUserId)}
                  </span>
                  <span className="ml-2 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[12px] text-[var(--color-text-secondary)]">
                    {item.orderSeq === 1
                      ? t('stylingReview.firstOrder')
                      : t('stylingReview.nthOrder', { count: item.orderSeq })}
                  </span>
                  <span className="mt-1 block text-[13px] text-[var(--color-text-muted)]">
                    {body ?? t('stylingReview.noKarte')}
                    {item.planName === null ? '' : ` / ${item.planName}`}
                    {item.stylistName === null ? '' : ` / ${item.stylistName}`}
                  </span>
                </span>
                <span className="w-[120px] shrink-0">
                  {item.myVerdict === null ? (
                    <span className="text-[14px] text-[var(--color-text-muted)]">
                      {t('stylingReview.notJudged')}
                    </span>
                  ) : (
                    <VerdictBadge
                      verdict={item.myVerdict}
                      label={
                        item.myVerdict === 'good'
                          ? t('stylingReview.verdict.good')
                          : t('stylingReview.verdict.bad')
                      }
                    />
                  )}
                </span>
                <span className="w-[130px] shrink-0">
                  {item.customerVerdict === null ? (
                    <span className="text-[14px] text-[var(--color-text-muted)]">
                      {t('stylingReview.waitingAnswer')}
                    </span>
                  ) : (
                    <VerdictBadge
                      verdict={item.customerVerdict}
                      label={
                        item.customerVerdict === 'good'
                          ? t('stylingReview.answer.good')
                          : t('stylingReview.answer.bad')
                      }
                    />
                  )}
                </span>
                <span className="w-[28px] shrink-0 text-center text-[18px] font-semibold">
                  {item.myVerdict !== null && item.customerVerdict !== null ? (
                    <span
                      className={
                        isMismatched(item)
                          ? 'text-[var(--color-error)]'
                          : 'text-[var(--color-success)]'
                      }
                    >
                      {isMismatched(item) ? '✕' : '✓'}
                    </span>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">→</span>
                  )}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
